import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { migrateToDatabase, checkMigrationData } from '../utils/migrationUtil';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import './MigrationPage.css';

const MigrationPage = () => {
  const [migrationData, setMigrationData] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Check for data to migrate on mount
  useEffect(() => {
    const data = checkMigrationData();
    setMigrationData(data);
  }, []);
  
  const handleMigration = async () => {
    if (!isAuthenticated || !user) {
      setError('You must be logged in to migrate data');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await migrateToDatabase(user.id);
      setMigrationResult(result);
    } catch (err) {
      console.error('Migration error:', err);
      setError(err.message || 'Failed to migrate data');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="migration-page">
      <div className="container">
        <h1 className="page-title">Data Migration</h1>
        
        <Card className="migration-card">
          <div className="migration-content">
            <h2>Migrate Your Data to the Cloud</h2>
            <p>
              We've upgraded Sign in Seconds to use a secure cloud database. This allows you to access your documents from any device and ensures your data is safely backed up.
            </p>
            
            {error && (
              <Alert 
                type="error" 
                title="Migration Error" 
                message={error} 
                className="mb-md"
              />
            )}
            
            {migrationResult && migrationResult.success && (
              <Alert 
                type="success" 
                title="Migration Successful" 
                message={`Successfully migrated ${migrationResult.migrated.documents.success} documents and ${migrationResult.migrated.signatures.success} signatures to the cloud.`} 
                className="mb-md"
              />
            )}
            
            {migrationResult && !migrationResult.success && (
              <Alert 
                type="error" 
                title="Migration Failed" 
                message={migrationResult.error || 'An unknown error occurred during migration'} 
                className="mb-md"
              />
            )}
            
            {!migrationResult && migrationData && (
              <div className="migration-info">
                <h3>Data Available for Migration</h3>
                <ul className="migration-list">
                  <li>
                    <span className="migration-item-label">Documents:</span>
                    <span className="migration-item-value">{migrationData.counts.documents}</span>
                  </li>
                  <li>
                    <span className="migration-item-label">Signatures:</span>
                    <span className="migration-item-value">{migrationData.counts.signatures}</span>
                  </li>
                  <li>
                    <span className="migration-item-label">User Preferences:</span>
                    <span className="migration-item-value">{migrationData.counts.hasPreferences ? 'Yes' : 'No'}</span>
                  </li>
                </ul>
              </div>
            )}
            
            {!migrationResult && !migrationData?.hasData && (
              <Alert 
                type="info" 
                title="No Data to Migrate" 
                message="We couldn't find any data in your browser's local storage to migrate." 
                className="mb-md"
              />
            )}
            
            <div className="migration-actions">
              {!migrationResult && migrationData?.hasData && (
                <Button 
                  onClick={handleMigration}
                  loading={loading}
                  disabled={loading || !isAuthenticated}
                >
                  Migrate Data to Cloud
                </Button>
              )}
              
              <Button 
                variant="secondary" 
                onClick={() => navigate('/')}
              >
                {migrationResult ? 'Continue to Dashboard' : 'Skip for Now'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MigrationPage;
